import React, { useContext, useState } from "react";

import { useAppStore } from "../../../../../context/store";
import { getUsernameAndUserIdFormat } from "../../../../../utilies/Utility";
import { Dialog, Portal, Field, CloseButton, Button, Input, VStack } from "@chakra-ui/react";

import { useEffect } from "react";
import { Cartographic, JulianDate, Math as CesiumMath, Cartesian3, PolygonHierarchy } from "cesium";
import { useMapObjects } from "../../../../../context/MapObjectsContext";

const LayersEditCoordinate = () => {
  // state
  const isOpenEditCoordianteModal = useAppStore((s) => s.isOpenEditCoordianteModal);
  const editCoordianteModalData = useAppStore((s) => s.editCoordianteModalData);
  const userData = useAppStore((s) => s.userData);

  // refs / setters
  const setIsOpenEditCoordianteModal = useAppStore.getState().setIsOpenEditCoordianteModal;
  const viewerRef = useAppStore.getState().viewerRef;
  const { setMapObjects, mapObjectsRef } = useMapObjects();

  const [coord, setCoord] = useState({
    lat: 0.0,
    lng: 0.0,
    z: 0.0,
  });

  const setModifier = (entity) => {
    const modifier = getUsernameAndUserIdFormat(userData);
    if (!modifier || !entity) return;

    const now = JulianDate.now();
    const currentProps = entity.properties?.getValue ? entity.properties.getValue(now) || {} : entity.properties || {};
    entity.properties = {
      ...currentProps,
      modifier,
    };
  };

  const update = () => {
    // --- 1) Guard clauses and inputs --------------------------------------------------------------
    const data = editCoordianteModalData; // stored from onPointContextClick

    // --- BULK HEIGHT MODE (selection p_p / p_f) ---------------------------------------------------
    if (data?.mode === "bulk_height" && data?.selection?.results) {
      const height = Number(coord.z);
      if (!Number.isFinite(height)) {
        console.warn("Invalid height:", coord.z);
        return;
      }

      const viewer = viewerRef?.current;
      const results = data.selection.results;

      const updatedEntities = new Set();

      const toHeight = (cartesian) => {
        const carto = Cartographic.fromCartesian(cartesian);
        return Cartesian3.fromRadians(carto.longitude, carto.latitude, height);
      };

      const findEntityById = (id) => {
        if (!id) return null;

        const fromViewer = viewer?.entities?.getById?.(id);
        if (fromViewer) return fromViewer;

        const list = mapObjectsRef?.current || [];
        for (const obj of list) {
          const ent = obj?.dataSource?.entities?.getById?.(id);
          if (ent) return ent;
        }
        return null;
      };

      const resolveEntity = (maybe) => {
        if (!maybe) return null;

        // already a Cesium Entity
        if (maybe.id && (maybe.polyline || maybe.polygon || maybe.position)) return maybe;

        if (maybe.entity) return resolveEntity(maybe.entity);

        const id = maybe.entityId ?? maybe.parentId ?? maybe.featureId ?? maybe.id;

        return findEntityById(id);
      };

      const each = (collection, fn) => {
        if (!collection) return;
        if (Array.isArray(collection)) {
          collection.forEach((v, i) => fn(v, i));
          return;
        }
        if (typeof collection === "object") {
          Object.keys(collection).forEach((k) => fn(collection[k], k));
        }
      };

      const getPolylinePositions = (entity) => {
        const now = JulianDate.now();
        const cur = entity.polyline.positions?.getValue?.(now) ?? entity.polyline.positions?.getValue?.() ?? entity.polyline.positions;
        return Array.isArray(cur) ? cur : [];
      };

      const getPolygonFlatPositions = (entity) => {
        const now = JulianDate.now();
        const hier = entity.polygon.hierarchy?.getValue?.(now) ?? entity.polygon.hierarchy?.getValue?.() ?? entity.polygon.hierarchy;
        const flat = hier?.positions ? [...hier.positions] : [];
        if (flat.length > 2 && Cartesian3.equals(flat[0], flat[flat.length - 1])) flat.pop();
        return flat;
      };

      const nearestIndex = (positions, target) => {
        if (!positions.length) return null;
        let bestI = 0;
        let bestD = Number.POSITIVE_INFINITY;
        for (let i = 0; i < positions.length; i++) {
          const d = Cartesian3.distance(positions[i], target);
          if (d < bestD) {
            bestD = d;
            bestI = i;
          }
        }
        return bestI;
      };

      const guessVertexIndex = (entity, pt) => {
        const lon = Number(pt?.lon);
        const lat = Number(pt?.lat);
        if (!Number.isFinite(lon) || !Number.isFinite(lat)) return null;

        const target = Cartesian3.fromDegrees(lon, lat, 0);

        if (entity?.polyline) {
          const positions = getPolylinePositions(entity);
          return nearestIndex(positions, target);
        }

        if (entity?.polygon) {
          const flat = getPolygonFlatPositions(entity);
          return nearestIndex(flat, target);
        }

        return null;
      };

      const applyHeightToPolylineVertex = (entity, idx) => {
        const now = JulianDate.now();
        const current = entity.polyline.positions?.getValue?.(now) ?? entity.polyline.positions?.getValue?.() ?? entity.polyline.positions;
        const positions = Array.isArray(current) ? current.slice() : [];
        if (!positions[idx]) return false;

        positions[idx] = toHeight(positions[idx]);
        entity.polyline.positions = positions;
        return true;
      };

      const applyHeightToPolygonVertex = (entity, idx) => {
        const now = JulianDate.now();
        const hier = entity.polygon.hierarchy?.getValue?.(now) ?? entity.polygon.hierarchy?.getValue?.() ?? entity.polygon.hierarchy;

        const flat = hier?.positions ? [...hier.positions] : [];
        if (flat.length === 0) return false;

        const isClosed = flat.length > 2 && Cartesian3.equals(flat[0], flat[flat.length - 1]);
        if (isClosed) flat.pop();

        if (!flat[idx]) return false;

        flat[idx] = toHeight(flat[idx]);
        const closed = flat[0] ? [...flat, flat[0]] : flat;

        entity.polygon.hierarchy = new PolygonHierarchy(closed);
        return true;
      };

      const applyHeightToPoint = (entity) => {
        const now = JulianDate.now();
        const p = entity.position?.getValue?.(now) ?? entity.position?.getValue?.() ?? entity.position;
        if (!p) return false;

        entity.position = toHeight(p);
        return true;
      };

      const applyTarget = (entity, idx) => {
        if (!entity) return;

        let changed = false;

        if (entity.polyline && Number.isInteger(idx)) {
          changed = applyHeightToPolylineVertex(entity, idx);
        } else if (entity.polygon && Number.isInteger(idx)) {
          changed = applyHeightToPolygonVertex(entity, idx);
        } else if (entity.position) {
          changed = applyHeightToPoint(entity);
        }

        if (changed) {
          setModifier(entity);
          updatedEntities.add(entity);
        }
      };

      // p_p (Only Start or End)
      const pp = results.p_p;
      each(pp, (item) => {
        const start = item?.endpoints?.start;
        const end = item?.endpoints?.end;

        const entity = resolveEntity(item) || resolveEntity(start) || resolveEntity(end);

        if (!entity) return;

        if (entity.polyline) {
          const positions = getPolylinePositions(entity);
          if (!positions.length) return;

          if (start) applyTarget(entity, 0);
          if (end) applyTarget(entity, positions.length - 1);

          if (!start && !end) {
            const idx = guessVertexIndex(entity, item);
            if (Number.isInteger(idx)) applyTarget(entity, idx);
          }
          return;
        }

        if (entity.position) {
          applyTarget(entity, null);
          return;
        }
      });

      // p_f (Any Location points)
      const pf = results.p_f;
      each(pf, (item) => {
        const pts = item?.points;

        each(pts, (pt) => {
          const entity = resolveEntity(pt) || resolveEntity(item);
          if (!entity) return;

          const idx = pt?.index ?? pt?.vertexIndex ?? guessVertexIndex(entity, pt);

          applyTarget(entity, idx);
        });
      });

      // mark mapObjects as edited (for all updated entities)
      let temp = [...mapObjectsRef.current];
      for (const ent of updatedEntities) {
        for (const obj of temp) {
          if (obj.dataSource?.entities?.contains?.(ent)) {
            obj.is_edited = true;
            break;
          }
        }
      }
      setMapObjects(temp);

      try {
        viewerRef.current.scene.requestRender?.();
      } catch {}

      setIsOpenEditCoordianteModal(false);
      return;
    }

    if (!data || !data.entity) {
      console.warn("No edit target found.");
      return;
    }
    const { entity, index } = data; // index: vertex index for line/polygon
    if (entity.polyline || entity.polygon) {
      if (!Number.isInteger(index)) {
        console.warn("Missing/invalid vertex index.");
        return;
      }
    }

    // --- 2) Build new Cartesian3 from coord (lng, lat, height) -----------------------------------
    // NOTE: Cesium.Cartesian3.fromDegrees expects (longitude, latitude, height)
    const lat = Number(coord.lat);
    const lng = Number(coord.lng);
    const z = Number(coord.z);

    if (![lat, lng, z].every(Number.isFinite)) {
      console.warn("Invalid coord values:", coord);
      return;
    }

    const newCartesian = Cartesian3.fromDegrees(lng, lat, z);

    let temp = [...mapObjectsRef.current];
    for (const obj of temp) {
      if (obj.dataSource?.entities?.contains(entity)) {
        obj.is_edited = true;
        break;
      }
    }
    setMapObjects(temp);

    // --- 3) Update entity geometry per type -------------------------------------------------------
    if (entity.polyline) {
      // Read current positions (handle ConstantProperty/CallbackProperty)
      const now = JulianDate.now();
      const current = entity.polyline.positions?.getValue?.(now) ?? entity.polyline.positions?.getValue?.() ?? entity.polyline.positions;

      const positions = Array.isArray(current) ? current.slice() : [];
      if (!positions[index]) {
        console.warn("Vertex index out of range for polyline.");
        return;
      }

      // Replace the vertex and assign back
      positions[index] = newCartesian;
      entity.polyline.positions = positions; // constant array is fine here
      setModifier(entity);
    } else if (entity.polygon) {
      // Read hierarchy and flatten (drop closing duplicate if present)
      const now = JulianDate.now();
      const hier = entity.polygon.hierarchy?.getValue?.(now) ?? entity.polygon.hierarchy?.getValue?.() ?? entity.polygon.hierarchy;

      const flat = hier?.positions ? [...hier.positions] : [];
      if (flat.length === 0) return;

      const isClosed = flat.length > 2 && Cartesian3.equals(flat[0], flat[flat.length - 1]);
      if (isClosed) flat.pop(); // remove duplicated closing point

      if (!flat[index]) {
        console.warn("Vertex index out of range for polygon.");
        return;
      }

      // Replace the vertex and re-close the ring
      flat[index] = newCartesian;
      const closed = flat[0] ? [...flat, flat[0]] : flat;

      // Assign back as PolygonHierarchy
      entity.polygon.hierarchy = new PolygonHierarchy(closed);
      setModifier(entity);
    } else if (entity.position) {
      // Simple point entity: set its position
      entity.position = newCartesian;
      setModifier(entity);
    } else {
      console.warn("Unsupported entity geometry type.");
      return;
    }

    // --- 4) (Optional) normalize state and request render -----------------------------------------
    // Keep state aligned with normalized numbers (optional)
    // setCoord(prev => ({ ...prev, lat, lng, z }));

    // If your app uses requestRenderMode=true, force a render so the change appears immediately.
    try {
      viewerRef.current.scene.requestRender?.();
    } catch (e) {}
    setIsOpenEditCoordianteModal(false);
  };

  useEffect(() => {
    if (!isOpenEditCoordianteModal) return;

    const data = editCoordianteModalData;
    const position = data?.position;
    if (!position) return;

    const carto = Cartographic.fromCartesian(position);

    const latDeg = CesiumMath.toDegrees(carto.latitude);
    const lngDeg = CesiumMath.toDegrees(carto.longitude);
    const zMeters = carto.height;

    setCoord({
      lat: +latDeg.toFixed(7),
      lng: +lngDeg.toFixed(7),
      z: +zMeters.toFixed(2),
    });
  }, [isOpenEditCoordianteModal]);

  return (
    <Dialog.Root
      lazyMount
      open={isOpenEditCoordianteModal}
      onOpenChange={(e) => {
        setIsOpenEditCoordianteModal(e.open);
      }}
    >
      <Portal>
        <Dialog.Backdrop />
        <Dialog.Positioner>
          <Dialog.Content>
            <Dialog.Header>
              <VStack gap={0}>
                <Dialog.Title textAlign={"left"} width={"full"}>
                  Edit Coordinate
                </Dialog.Title>
              </VStack>
            </Dialog.Header>
            <Dialog.Body>
              <Field.Root>
                <Field.Label>Lat</Field.Label>
                <Input
                  type="number"
                  placeholder="Enter lat ..."
                  value={coord.lat}
                  onChange={(v) => {
                    setCoord((prev) => ({
                      ...prev,
                      lat: Number(v.target.value),
                    }));
                  }}
                />
              </Field.Root>
              <Field.Root>
                <Field.Label>Lon</Field.Label>
                <Input
                  type="number"
                  placeholder="Enter lon ..."
                  value={coord.lng}
                  onChange={(v) => {
                    setCoord((prev) => ({
                      ...prev,
                      lng: Number(v.target.value),
                    }));
                  }}
                />
              </Field.Root>
              <Field.Root>
                <Field.Label>Hieght</Field.Label>
                <Input
                  type="number"
                  placeholder="Enter height ..."
                  value={coord.z}
                  onChange={(v) => {
                    setCoord((prev) => ({
                      ...prev,
                      z: Number(v.target.value),
                    }));
                  }}
                />
              </Field.Root>
            </Dialog.Body>
            <Dialog.Footer>
              <Dialog.ActionTrigger asChild>
                <Button variant="outline">Cancel</Button>
              </Dialog.ActionTrigger>
              <Button
                onClick={() => {
                  update();
                }}
              >
                Update
              </Button>
            </Dialog.Footer>
            <Dialog.CloseTrigger asChild>
              <CloseButton size="sm" />
            </Dialog.CloseTrigger>
          </Dialog.Content>
        </Dialog.Positioner>
      </Portal>
    </Dialog.Root>
  );
};

export default LayersEditCoordinate;
